import axios from 'axios'
import { useAuthStore } from '@/shared/store/authStore'
import { toast } from '@/shared/hooks/use-toast'
// Not a React component, so there is no useTranslation() here — use the i18next
// instance directly. Imported for its side effect elsewhere too (test/setup.ts).
import i18n from '@/i18n'
import { rememberSessionEndNotice } from '@/features/auth/sessionEndNotice'

export const apiClient = axios.create({
  baseURL: '/api/v1',
  // Without this axios won't include the studioflow_refresh cookie on
  // /auth/refresh in dev (Vite proxy is technically same-origin from the
  // browser's perspective so this is belt-and-braces). In prod both SPA
  // and API are on studioflow.ch, so the cookie travels regardless.
  withCredentials: true,
})

apiClient.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  // Tell the backend which language this page is CURRENTLY rendering in, rather
  // than letting it guess. A few responses carry text the app paints straight
  // into the page beside t() output — the attention panel's item titles are the
  // big one — and those have to be in the same language as everything around
  // them. i18n.language is the single fact that decides that here (uiLocale →
  // browser → en-US), so it is the fact the server should resolve from too;
  // `StudioLocaleResolver.forRequestUI()` reads exactly this header. Without it
  // a German-speaking studio's English UI came back with German attention items
  // (F5, p3-phone), because the server fell back to the studio's CLIENT-facing
  // document locale. Accept-Language is CORS-safelisted, so no preflight.
  config.headers['Accept-Language'] = i18n.language
  return config
})

/**
 * The one `/auth/refresh` allowed to be in flight at a time, app-wide.
 *
 * **Why this has to be shared rather than local to the interceptor.** Refresh tokens
 * rotate: presenting one revokes its row and mints a successor chained to it
 * (`RefreshTokenService.rotate`). That model assumes ONE presentation per token. Two
 * concurrent presentations of the same cookie both find the row unrevoked, and both
 * mint a successor — so one parent ends up with **two** children, only one of whose
 * cookies the browser keeps.
 *
 * The backend's own benign-race escape hatch is what that breaks. When a revoked token
 * is presented inside the 15 s rotation grace, `graceSuccessorOf` calls
 * `findByRotatedFromId`, which is declared and typed as returning AT MOST ONE row
 * ("one row is minted per rotation"). With a forked chain it matches two and the query
 * throws — a 500 out of `/auth/refresh`, which this interceptor can only read as "the
 * session is gone", so it clears the store and sends the whole app to `/login`.
 *
 * Measured on this codebase before the fix: every cold boot forked the chain, because
 * `ProtectedRoute`'s boot refresh went straight to `apiClient.post('/auth/refresh')`
 * while the interceptor kept its own separate guard, and React's StrictMode double-
 * invokes that effect on top. Two rows with the same `rotated_from_id`, every time.
 *
 * So both callers go through here. The promise is shared while in flight and cleared
 * on settle, which is the whole mechanism: a refresh that is genuinely needed later
 * still happens, a refresh that merely coincides with one already running does not.
 */
export interface RefreshPayload {
  accessToken: string
  /** Absent when the server had nothing new to say about the membership. */
  member?: ReturnType<typeof useAuthStore.getState>['teamMember']
}

let inFlightRefresh: Promise<RefreshPayload> | null = null

/**
 * A refresh the server refused for VOLUME (429), as opposed to refused as a session. Only a
 * refusal of the session itself (no cookie, unknown/expired/revoked cookie, access changed)
 * ends one; "you are asking too often" says nothing about whether the cookie is good, and
 * reading it as "signed out" dropped people on the landing page after about ten reloads in a
 * minute (RB-28 item 3, sweep row 12.8).
 */
export function isRefreshRateLimited(err: unknown): boolean {
  return (err as { response?: { status?: number } } | null)?.response?.status === 429
}

/** How long to wait before the one retry: the server's Retry-After, clamped to something a person will sit through. */
const REFRESH_BACKOFF_DEFAULT_MS = 2_000
const REFRESH_BACKOFF_MAX_MS = 10_000
export function refreshRetryDelayMs(err: unknown): number {
  const raw = (err as { response?: { headers?: Record<string, unknown> } } | null)
    ?.response?.headers?.['retry-after']
  const seconds = typeof raw === 'string' || typeof raw === 'number' ? Number(raw) : NaN
  if (!Number.isFinite(seconds) || seconds <= 0) return REFRESH_BACKOFF_DEFAULT_MS
  return Math.min(Math.ceil(seconds * 1000), REFRESH_BACKOFF_MAX_MS)
}

async function postRefresh(): Promise<RefreshPayload> {
  try {
    return (await apiClient.post('/auth/refresh')).data
  } catch (err) {
    if (!isRefreshRateLimited(err)) throw err
    // One retry after the wait, still inside the shared in-flight promise, so everything that
    // asked meanwhile joins it and the cookie is never presented twice at once. A 429 never
    // reached the handler, so the rotating cookie has not been spent. If the retry is refused
    // too, the 429 propagates and callers keep the session (see isRefreshRateLimited).
    await new Promise((resolve) => setTimeout(resolve, refreshRetryDelayMs(err)))
    return (await apiClient.post('/auth/refresh')).data
  }
}

export function refreshSession(): Promise<RefreshPayload> {
  if (inFlightRefresh) return inFlightRefresh
  inFlightRefresh = postRefresh().finally(() => { inFlightRefresh = null })
  return inFlightRefresh
}

let isRefreshing = false
// Both arms are captured: a failed refresh must REJECT everything queued behind
// it, or the queued requests hang forever (spinner-locked buttons, mutations
// whose onError never fires) whenever the /login navigation is slow or the app
// is already on /login. The queue was also never cleared on the failure path, so
// a later successful refresh replayed them with a fresh token, re-issuing
// requests from a dead session.
let refreshQueue: Array<{ resolve: (token: string) => void; reject: (err: unknown) => void }> = []

function drainQueue(err: unknown, token?: string) {
  const queued = refreshQueue
  refreshQueue = []
  for (const q of queued) {
    if (err) q.reject(err)
    else q.resolve(token!)
  }
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config

    // 423 LOCKED — the org's lifecycle refuses writes. Three states reach this,
    // and they are three different sentences, so the header decides which:
    //
    //   CANCELLED_GRACE   the subscription ended; read-only for 30 days (D3).
    //                     This is now the COMMON case — a trial's grace period
    //                     is writable to its last day, so a cancelled studio is
    //                     the one that actually meets this gate day to day.
    //   PENDING_DELETION  the studio has closed and its data has a date on it.
    //   READ_ONLY         no writer any more; a legacy row from an old sweep.
    //                     Its message is the generic one, because we genuinely
    //                     cannot tell that owner why they are locked.
    //
    // Every one of them names the self-service door rather than support: the
    // billing panel is exempt from this gate precisely so paying works from
    // inside the lock. The banner is already showing; this toast is for the
    // action the user just attempted, so spinners don't hang silently. Keep
    // rejecting so the calling mutation still flips its error state.
    if (error.response?.status === 423) {
      const state = error.response.headers?.['x-lifecycle-state'] as string | undefined
      const key =
        state === 'PENDING_DELETION'
          ? 'pendingDeletion'
          : state === 'CANCELLED_GRACE'
            ? 'cancelledGrace'
            : 'locked'
      toast({
        title: i18n.t(`common:common.lifecycle.${key}Title`),
        description: i18n.t(`common:common.lifecycle.${key}Description`),
        variant: 'destructive',
        // Without this the toast below never survives: we re-reject, the
        // feature's own onError toasts its hardcoded fallback microseconds
        // later, and TOAST_LIMIT = 1 newest-wins throws THIS one away. The
        // Clients page showed "Could not create client." and nothing about the
        // lock (p2-subscription F3). `priority: 'high'` tells the toast store
        // that this message explains the failure the next one merely reports.
        priority: 'high',
      })
      return Promise.reject(error)
    }

    if (error.response?.status !== 401) {
      return Promise.reject(error)
    }

    // Auth-critical paths return 401 as a legitimate "wrong credentials" signal.
    // Let the calling code's catch handler deal with it — don't try to refresh
    // or redirect. The interceptor should only fire for protected-resource 401s
    // where the session genuinely expired.
    const url = original.url ?? ''
    const isAuthCriticalPath =
      url.startsWith('/auth/login') ||
      url.startsWith('/auth/refresh') ||
      url.startsWith('/auth/passkey/') ||
      url.startsWith('/auth/oauth/') ||
      url.startsWith('/auth/mfa/') ||
      url.startsWith('/auth/password-reset')
    if (isAuthCriticalPath) {
      return Promise.reject(error)
    }

    // Already retried once with a refreshed token and still 401 — the session
    // is genuinely severed (e.g. the member was deactivated and the refresh
    // race closed after the token was renewed). Treat as terminal: without
    // this, the rejection just left the user in a half-dead session.
    if (original._retry) {
      drainQueue(error)
      useAuthStore.getState().clearAuth()
      window.location.href = '/login'
      return Promise.reject(error)
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        refreshQueue.push({
          resolve: (token) => {
            original.headers.Authorization = `Bearer ${token}`
            resolve(apiClient(original))
          },
          reject,
        })
      })
    }

    original._retry = true
    isRefreshing = true

    try {
      // Refresh tokens live in the HttpOnly studioflow_refresh cookie now —
      // we don't have to (and can't) read them from JS. The browser attaches
      // the cookie because /api/v1/auth/refresh is in the cookie's Path.
      //
      // Through refreshSession(), not apiClient directly: a 401 arriving while
      // ProtectedRoute's boot refresh is still in flight must JOIN it, not present
      // the same cookie a second time and fork the rotation chain.
      const data = await refreshSession()
      const newToken: string = data.accessToken
      const freshMember = data.member ?? useAuthStore.getState().teamMember!
      useAuthStore.getState().setAuth(newToken, freshMember)

      drainQueue(null, newToken)

      original.headers.Authorization = `Bearer ${newToken}`
      return apiClient(original)
    } catch (refreshError) {
      // A refresh refused for volume (429, after its one retry) is not a dead session: keep the
      // store and the page, fail only the request that triggered it, and let the next call
      // refresh again. Everything below this guard is for a session that really ended.
      if (isRefreshRateLimited(refreshError)) {
        drainQueue(refreshError)
        return Promise.reject(error)
      }
      // IN-116 — say WHY, once. The server marks exactly one refusal: a refresh
      // session that predates a change to this membership's access
      // (`ErrorResponse.code === 'ACCESS_CHANGED'`). Every other failure stays an
      // ordinary expiry and the sign-in page says nothing extra. The studio NAME is
      // read from our own store before `clearAuth()` wipes it — the response carries
      // no name, and must not.
      const refusalCode = (refreshError as { response?: { data?: { code?: string } } })
        ?.response?.data?.code
      if (refusalCode === 'ACCESS_CHANGED') {
        rememberSessionEndNotice({
          reason: 'ACCESS_CHANGED',
          studio: useAuthStore.getState().teamMember?.orgName,
        })
      }
      // Reject the queue FIRST — before navigating away — so in-flight mutations
      // settle and their error handlers run even if the navigation is slow or
      // the app is already sitting on /login.
      drainQueue(refreshError)
      useAuthStore.getState().clearAuth()
      window.location.href = '/login'
      return Promise.reject(error)
    } finally {
      isRefreshing = false
    }
  }
)
